import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LogOut, RefreshCw, Inbox } from "lucide-react";
import { useAuth } from "@/components/site/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [quotes, setQuotes] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [q, a] = await Promise.all([
        axios.get(`${API}/quotes`, { withCredentials: true }),
        axios.get(`${API}/careers`, { withCredentials: true }),
      ]);
      setQuotes(q.data);
      setApps(a.data);
    } catch {
      setQuotes([]);
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } finally {
      setUser(false);
      navigate("/login");
    }
  };

  const resend = async (id) => {
    try {
      await axios.post(`${API}/quotes/${id}/resend-notification`, {}, { withCredentials: true });
      setQuotes(quotes.map((q) => (q.id === id ? { ...q, email_sent: true } : q)));
    } catch {
      alert("Re-send failed. Please try again or call the customer directly.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="admin-page">
      <header className="bg-[#0F172A] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-logo-link">
              <img src="/assets/aegis-logo-dark.webp" alt="Aegis Subsurface LLC logo" className="h-12 w-auto" />
            </Link>
            <span className="text-white font-heading font-bold uppercase tracking-wider text-sm hidden sm:inline">
              Quote Requests
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm hidden sm:inline" data-testid="admin-user-email">{user?.email}</span>
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#FF5A00] transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0F172A]" data-testid="admin-heading">
              Quote Requests
            </h1>
            <p className="text-sm text-slate-600 mt-1" data-testid="admin-count">
              {quotes.length} request{quotes.length === 1 ? "" : "s"} received
            </p>
          </div>
          <button
            onClick={load}
            data-testid="admin-refresh-button"
            className="inline-flex items-center gap-2 bg-[#0F172A] text-white text-sm font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm hover:bg-[#1E293B] transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-slate-500 text-sm" data-testid="admin-loading">Loading...</p>
        ) : quotes.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-sm p-16 text-center" data-testid="admin-empty">
            <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-500">No quote requests yet. They'll appear here when customers submit the form.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`admin-quote-${q.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                  <div>
                    <h3 className="font-heading text-lg font-bold text-[#0F172A]">
                      {q.name} {q.company ? <span className="text-slate-500 font-medium">· {q.company}</span> : null}
                    </h3>
                    <p className="text-sm text-slate-600 mt-0.5">
                      {q.phone} · {q.email}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="inline-block bg-[#FF5A00]/10 text-[#FF5A00] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm">
                      {q.service}
                    </span>
                    {q.email_sent ? (
                      <span className="text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-sm" data-testid={`email-status-${q.id}`}>
                        Email alert sent
                      </span>
                    ) : (
                      <button
                        onClick={() => resend(q.id)}
                        className="text-xs font-bold uppercase tracking-wider text-white bg-red-600 hover:bg-red-700 px-2.5 py-1 rounded-sm transition-colors"
                        data-testid={`resend-alert-${q.id}`}
                      >
                        Resend Email Alert
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                  <div><span className="font-semibold text-[#0F172A]">Address:</span> <span className="text-slate-600">{q.project_address}</span></div>
                  <div><span className="font-semibold text-[#0F172A]">Quantity:</span> <span className="text-slate-600">{q.quantity || "—"}</span></div>
                  <div><span className="font-semibold text-[#0F172A]">Depth:</span> <span className="text-slate-600">{q.depth || "—"}</span></div>
                  <div><span className="font-semibold text-[#0F172A]">Preferred:</span> <span className="text-slate-600">{q.preferred_date || "—"}</span></div>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{q.description}</p>
                <p className="mt-3 text-xs text-slate-400 font-mono-tech">
                  Submitted {new Date(q.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-16">
          <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0F172A]" data-testid="admin-apps-heading">
            Job Applications
          </h2>
          <p className="text-sm text-slate-600 mt-1" data-testid="admin-apps-count">
            {apps.length} application{apps.length === 1 ? "" : "s"} received
          </p>

          {apps.length === 0 ? (
            <div className="mt-6 bg-white border border-slate-200 rounded-sm p-12 text-center" data-testid="admin-apps-empty">
              <Inbox className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">No job applications yet. They'll appear here when candidates apply.</p>
            </div>
          ) : (
            <div className="mt-6 space-y-4" data-testid="admin-apps-list">
              {apps.map((a) => (
                <div key={a.id} className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`admin-app-${a.id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div>
                      <h3 className="font-heading text-lg font-bold text-[#0F172A]">{a.name}</h3>
                      <p className="text-sm text-slate-600 mt-0.5">{a.phone} · {a.email}</p>
                    </div>
                    <span className="inline-block bg-[#0F172A]/5 text-[#0F172A] text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm shrink-0 border border-slate-200">
                      {a.position}
                    </span>
                  </div>
                  {a.experience && (
                    <p className="mt-3 text-sm"><span className="font-semibold text-[#0F172A]">Experience:</span> <span className="text-slate-600">{a.experience}</span></p>
                  )}
                  {a.message && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.message}</p>}
                  <p className="mt-3 text-xs text-slate-400 font-mono-tech">
                    Applied {new Date(a.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
